// ═══════════════════════════════════════════════════════════
// HomeView — Apple-inspired white theme, bilingual
// ═══════════════════════════════════════════════════════════

import { motion } from "framer-motion";
import { Search, Building2, ChevronLeft, ChevronRight, Smartphone } from "lucide-react";
import { NAMED, TOTAL_FLOORS } from "@/lib/data";
import { useLang } from "@/lib/i18n";
import type { ViewType } from "./BottomNav";

const HERO_BG = "/cgb-building-hero.webp";

interface Props {
  onNavigate: (v: ViewType) => void;
}

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, delay, ease: [0.0, 0.0, 0.2, 1.0] as const },
});

export default function HomeView({ onNavigate }: Props) {
  const { t, isRtl } = useLang();
  const Chevron = isRtl ? ChevronLeft : ChevronRight;

  return (
    <div className="min-h-screen flex flex-col pt-10 pb-6">
      {/* Header — eyebrow + title */}
      <div className="text-center px-6 mb-6">
        <motion.p
          {...fadeUp(0.05)}
          className={`text-[10px] text-[#6B7280] mb-3 font-medium ${isRtl ? "" : "tracking-[2px] uppercase"}`}
        >
          {t.orgEyebrow}
        </motion.p>
        <motion.h1 {...fadeUp(0.1)} className="text-[26px] font-bold leading-tight text-[#1A2747]">
          {t.orgTitleLine1}
        </motion.h1>
        <motion.h1 {...fadeUp(0.13)} className="text-[26px] font-bold leading-tight text-[#1A2747]">
          {t.orgTitleLine2}
        </motion.h1>
      </div>

      {/* Centered building illustration — transparent, works on any theme */}
      <motion.div
        {...fadeUp(0.18)}
        className="flex justify-center mb-8"
      >
        <img
          src={HERO_BG}
          alt={t.orgEyebrow}
          width="280"
          height="360"
          fetchPriority="high"
          decoding="async"
          className="w-[70%] max-w-[300px] h-auto object-contain select-none"
          draggable={false}
        />
      </motion.div>

      {/* Stats row */}
      <div className="px-4 mb-5">
        <motion.div {...fadeUp(0.24)} className="grid grid-cols-2 gap-3">
          {[
            { value: TOTAL_FLOORS, label: t.statFloors },
            { value: NAMED.length, label: t.statDepts },
          ].map((stat, i) => (
            <div key={i} className="glass-card py-4 px-2 text-center">
              <div className="text-[24px] font-bold text-[#1A2747] font-mono-floor leading-none">
                {stat.value}
              </div>
              <div className="text-[10px] text-[#6B7280] mt-1.5 font-medium">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Search bar */}
      <motion.div {...fadeUp(0.28)} className="px-4 mb-3">
        <button
          onClick={() => onNavigate("search")}
          style={{ touchAction: "manipulation" }}
          className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl ${isRtl ? "text-right" : "text-left"}
            bg-white border border-[rgba(15,23,42,0.06)]
            shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_20px_rgba(15,23,42,0.04)]
            hover:shadow-[0_2px_4px_rgba(15,23,42,0.05),0_12px_28px_rgba(15,23,42,0.07)]
            active:scale-[0.99] transition-all duration-200`}
        >
          <span className="flex-1 text-[12px] text-[#9CA3AF]">{t.searchPlaceholder}</span>
          <Search size={15} className="text-[#1A2747] opacity-70 flex-shrink-0" />
        </button>
      </motion.div>

      {/* Quick actions */}
      <div className="px-4 flex flex-col gap-2.5 mb-6">
        <motion.button
          {...fadeUp(0.32)}
          onClick={() => onNavigate("browse")}
          style={{ touchAction: "manipulation" }}
          className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl ${isRtl ? "text-right" : "text-left"}
            bg-white border border-[rgba(15,23,42,0.06)]
            shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_20px_rgba(15,23,42,0.04)]
            hover:shadow-[0_2px_4px_rgba(15,23,42,0.05),0_12px_28px_rgba(15,23,42,0.07)]
            active:scale-[0.99] transition-all duration-200`}
        >
          <Chevron size={15} className="text-[#9CA3AF] flex-shrink-0" />
          <div className="flex-1">
            <div className="text-[13px] font-semibold text-[#1A2747]">{t.browseAllFloors}</div>
            <div className="text-[10px] text-[#6B7280] mt-0.5">{t.browseAllFloorsSub}</div>
          </div>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
            bg-[rgba(74,144,217,0.10)]">
            <Building2 size={17} className="text-[#1A2747]" />
          </div>
        </motion.button>

        <motion.button
          {...fadeUp(0.36)}
          onClick={() => onNavigate("search")}
          style={{ touchAction: "manipulation" }}
          className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl ${isRtl ? "text-right" : "text-left"}
            bg-white border border-[rgba(15,23,42,0.06)]
            shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_20px_rgba(15,23,42,0.04)]
            hover:shadow-[0_2px_4px_rgba(15,23,42,0.05),0_12px_28px_rgba(15,23,42,0.07)]
            active:scale-[0.99] transition-all duration-200`}
        >
          <Chevron size={15} className="text-[#9CA3AF] flex-shrink-0" />
          <div className="flex-1">
            <div className="text-[13px] font-semibold text-[#1A2747]">{t.advancedSearch}</div>
            <div className="text-[10px] text-[#6B7280] mt-0.5">{t.advancedSearchSub}</div>
          </div>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
            bg-[rgba(74,144,217,0.10)]">
            <Search size={17} className="text-[#1A2747]" />
          </div>
        </motion.button>

        <motion.a
          {...fadeUp(0.40)}
          href="/install"
          style={{ touchAction: "manipulation" }}
          className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl ${isRtl ? "text-right" : "text-left"}
            bg-white border border-[rgba(15,23,42,0.06)]
            shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_20px_rgba(15,23,42,0.04)]
            hover:shadow-[0_2px_4px_rgba(15,23,42,0.05),0_12px_28px_rgba(15,23,42,0.07)]
            active:scale-[0.99] transition-all duration-200`}
        >
          <Chevron size={15} className="text-[#9CA3AF] flex-shrink-0" />
          <div className="flex-1">
            <div className="text-[13px] font-semibold text-[#1A2747]">{t.printDirectory}</div>
            <div className="text-[10px] text-[#6B7280] mt-0.5">{t.printDirectorySub}</div>
          </div>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
            bg-[rgba(74,144,217,0.10)]">
            <Smartphone size={17} className="text-[#1A2747]" />
          </div>
        </motion.a>
      </div>

    </div>
  );
}
